import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { useServerURL } from '#components/ServerContext';

import { ApplicationDonnees } from './ApplicationDonnees';
import { NotificationsAppareil } from './NotificationsAppareil';
import type { Device } from './NotificationsAppareil';
import { Line, MtxPage, Section } from './ui';
import { useParcours } from './useParcours';

type Status = {
  checks: { name: string; ok: boolean; detail: string }[];
  calendarPath: string | null;
  timeZone: string;
  devices: Device[];
  journal: {
    at: string;
    channel: string;
    title: string;
    ok: boolean;
    detail: string;
  }[];
};

export function EtatPage() {
  const { t } = useTranslation();
  const serverUrl = useServerURL();
  const [status, setStatus] = useState<Status | null>(null);
  const [failed, setFailed] = useState(false);
  const [testing, setTesting] = useState(false);
  // The self-checks of the calculation run on this device, not on the server.
  const parcours = useParcours();
  const wrong = parcours.checks.filter(check => !check.ok);
  const calculation = {
    name: t('Calcul des quinzaines'),
    ok: parcours.status === 'ready' && wrong.length === 0,
    detail:
      parcours.status === 'loading'
        ? t('calcul en cours…')
        : wrong.length > 0
          ? `${wrong[0].name} — ${wrong[0].detail}`
          : t('{{count}} contrôles sur {{count}}, sur {{pays}} paies', {
              count: parcours.checks.length,
              pays: parcours.steps.length,
            }),
  };

  async function load() {
    const result = await send('mtxbudget-server', { route: 'status' });
    if ('data' in result) {
      setStatus(result.data as Status);
      setFailed(false);
    } else {
      setFailed(true);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function test() {
    setTesting(true);
    await send('mtxbudget-server', { route: 'test' });
    await load();
    setTesting(false);
  }

  return (
    <MtxPage title={t('État de MTXbudget')}>
      <View style={{ gap: 12, paddingBottom: 20 }}>
        {failed && (
          <Section style={{ padding: 16 }}>
            <Text style={{ color: theme.errorText }}>
              {t('Le serveur ne répond pas : état indisponible.')}
            </Text>
          </Section>
        )}

        <ApplicationDonnees />

        {status && (
          <>
            <NotificationsAppareil devices={status.devices} onChange={load} />

            <Section
              title={t('Contrôles')}
              aside={t('{{ok}} sur {{total}}', {
                ok: [...status.checks, calculation].filter(c => c.ok).length,
                total: status.checks.length + 1,
              })}
            >
              {[...status.checks, calculation].map((check, index) => (
                <Line
                  key={check.name}
                  last={index === status.checks.length}
                  title={check.name}
                  detail={check.detail}
                  value={check.ok ? t('OK') : t('À voir')}
                  valueColor={
                    check.ok ? theme.noticeTextLight : theme.warningText
                  }
                />
              ))}
            </Section>

            {status.calendarPath && (
              <Section title={t('Calendrier abonnable')} style={{ gap: 0 }}>
                <Text
                  style={{
                    padding: 16,
                    fontSize: 13,
                    wordBreak: 'break-all',
                    userSelect: 'all',
                  }}
                >
                  {(serverUrl ?? '') + status.calendarPath}
                </Text>
              </Section>
            )}

            <Section title={t('Derniers envois')} aside={t('heures en UTC')}>
              {status.journal.length === 0 && (
                <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
                  <Trans>Aucun envoi pour l'instant.</Trans>
                </Text>
              )}
              {status.journal.map((entry, index) => (
                <Line
                  key={`${entry.at}-${entry.channel}`}
                  last={index === status.journal.length - 1}
                  title={entry.title}
                  detail={`${entry.at.slice(0, 16).replace('T', ' ')} · ${entry.channel} · ${entry.detail}`}
                  value={entry.ok ? t('OK') : t('Échec')}
                  valueColor={
                    entry.ok ? theme.noticeTextLight : theme.errorText
                  }
                />
              ))}
            </Section>

            <Button
              isDisabled={testing}
              onPress={() => void test()}
              style={{ minHeight: 48 }}
            >
              {testing
                ? t('Envoi en cours…')
                : t("Envoyer une notification d'essai")}
            </Button>
          </>
        )}
      </View>
    </MtxPage>
  );
}
